
import { Medication, TravelAdjustment } from '@/types/medication';

/**
 * Service responsible for handling timezone-related functionality
 * Supports both medication scheduling and analytics time tracking
 */
export class TimeZoneService {
  static calculateAdjustedSchedule(
    medication: Medication,
    departureTime: Date,
    arrivalTime: Date,
    originTimezone: string,
    destinationTimezone: string
  ): TravelAdjustment {
    const originOffset = new Date().getTimezoneOffset();
    const destOffset = new Date().toLocaleString("en-US", { timeZone: destinationTimezone });
    const hoursDiff = Math.round((new Date(destOffset).getTime() - new Date().getTime()) / (1000 * 60 * 60));

    // Calculate adjusted time based on medication's strict timing requirements
    const adjustedTime = medication.strictTiming
      ? this.calculateStrictTimingAdjustment(medication, hoursDiff)
      : this.calculateFlexibleTimingAdjustment(medication, hoursDiff);

    return {
      originalTime: medication.time,
      adjustedTime,
      localDate: arrivalTime.toISOString(),
      homeDate: departureTime.toISOString(),
      isTransitionDay: true,
      isBridgingDose: hoursDiff > (medication.maxTimeShift || 4)
    };
  }

  private static calculateStrictTimingAdjustment(medication: Medication, hoursDiff: number): string {
    // Implementation for medications requiring strict timing
    const originalHour = medication.time === 'morning' ? 8 : 20;
    return this.adjustHourSafely(originalHour, hoursDiff);
  }

  private static calculateFlexibleTimingAdjustment(medication: Medication, hoursDiff: number): string {
    // Implementation for medications with flexible timing
    const originalHour = medication.time === 'morning' ? 8 : 20;
    const adjustment = Math.min(hoursDiff, medication.maxTimeShift || 4);
    return this.adjustHourSafely(originalHour, adjustment);
  }

  private static adjustHourSafely(hour: number, adjustment: number): string {
    const newHour = (hour + adjustment + 24) % 24;
    return `${newHour.toString().padStart(2, '0')}:00`;
  }

  /**
   * Gets the user's local timezone
   * @returns The local timezone string (e.g., 'America/Los_Angeles')
   */
  public getUserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      
      return 'UTC'; // Default fallback
    }
  }

  /**
   * Converts a date to the user's local timezone
   * @param date Date to convert (default: current time)
   * @returns Date object in user's local timezone
   */
  public getUserLocalTime(date: Date = new Date()): Date {
    try {
      const userTimezone = this.getUserTimezone();
      const dateString = date.toLocaleString('en-US', { timeZone: userTimezone });
      return new Date(dateString);
    } catch (error) {
      
      return date; // Return the original date as fallback
    }
  }

  /**
   * Determines the time of day category based on hour
   * @param hour Hour in 24-hour format
   * @returns Category as 'morning', 'afternoon', 'evening', or 'night'
   */
  public getTimeOfDayCategory(hour: number): 'morning' | 'afternoon' | 'evening' | 'night' {
    if (hour >= 5 && hour < 12) {
      return 'morning';
    } else if (hour >= 12 && hour < 17) {
      return 'afternoon';
    } else if (hour >= 17 && hour < 21) {
      return 'evening';
    } else {
      return 'night';
    }
  }
}
